import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { useCustomerLeadQueries } from "@lib/apiQueries/useCustomerLeadQueries";
import { CustomerLead } from "@lib/types/CustomerLead";
import CustomerLeadModal from "@lib/components/PageComponents/Customer/CustomerLeadModal";
import { SharedContextProps } from "@lib/types/Context";
import { useTypedSelector } from "@lib/redux";

const SharedCustomerLeads: React.FC<SharedContextProps> = ({
  context,
  detailContext,
  customTitle,
  customerId,
  pageIsActive = true,
  withoutContainer = false,
  minusHeight,
}) => {
  const { messages } = useIntl();
  const { role } = useTypedSelector((state) => state.auth.user);

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer"],
    filter: initialFilters,
  });

  const {
    customerLeadsQuery,
    addCustomerLeadMutation,
    updateCustomerLeadMutation,
    deleteCustomerLeadMutation,
  } = useCustomerLeadQueries({
    params,
    messages,
    enabled: pageIsActive,
  });
  const { data, isLoading, isFetching } = customerLeadsQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (
      addCustomerLeadMutation.isSuccess ||
      updateCustomerLeadMutation.isSuccess ||
      deleteCustomerLeadMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addCustomerLeadMutation.isSuccess,
    updateCustomerLeadMutation.isSuccess,
    deleteCustomerLeadMutation.isSuccess,
  ]);

  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<CustomerLead>();

  useEffect(() => {
    if (!openModal) setSelected(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: CustomerLead) => {
    setSelected(item);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        detailContext,
        handleSelect,
        deleteQuery: deleteCustomerLeadMutation,
        openModal,
        apiData,
        params,
        messages,
        userRole: role,
      }),
    [
      apiData,
      deleteCustomerLeadMutation,
      openModal,
      handleSelect,
      params,
      role,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params?.filter, columns, hiddenFilters]);

  const title = customTitle || (messages["customer.list"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={!!withoutContainer}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={customerLeadsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          onChange={onChange}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          size="small"
          minusHeight={minusHeight}
        />
      </AppsContent>

      <CustomerLeadModal
        mutationQuery={
          selected ? updateCustomerLeadMutation : addCustomerLeadMutation
        }
        openModal={openModal}
        toggleModal={toggle}
        customerLead={selected}
      />
    </AppsContainer>
  );
};

export default SharedCustomerLeads;
